import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { navLinks, site } from "@/config/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/news?q=${encodeURIComponent(q)}` : "/news");
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      "relative whitespace-nowrap px-1 py-1 text-sm transition-colors",
      isActive ? "text-primary-600" : "text-foreground-700 hover:text-primary-600",
    ].join(" ");

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div
        className={`overflow-hidden bg-foreground-950 transition-all duration-300 ${
          scrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs text-background-200 md:px-6">
          <p className="flex items-center gap-2">
            <i className="ri-fire-line text-primary-400" aria-hidden="true" />
            {site.name} · {site.slogan}
          </p>
          <p className="hidden items-center gap-4 sm:flex">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" aria-hidden="true" />
              每日更新
            </span>
            <span>本站内容仅供娱乐参考，请理性吃瓜</span>
          </p>
        </div>
      </div>

      <div className="flex h-16 items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" onClick={() => setMenuOpen(false)}>
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-fire-line text-xl" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-black tracking-wide text-foreground-950">
              {site.name}
            </span>
            <span className="mt-1 hidden text-[11px] tracking-[0.2em] text-foreground-500 sm:block">
              GUA NEWS DAILY
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden items-center gap-7 md:flex">
          {navLinks.map((link) => (
            <NavLink key={link.to} to={link.to} className={linkClass} end={link.to === "/"}>
              {link.label}
            </NavLink>
          ))}
          <Link
            to="/news"
            className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            热榜
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden lg:flex lg:items-center">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 focus-within:border-primary-400">
              <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜瓜料 / 话题"
                aria-label="搜索瓜料"
                className="w-32 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none xl:w-44"
              />
            </div>
          </form>
          <Link
            to="/about"
            className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            我要投稿
          </Link>
          <button
            type="button"
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 md:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div
        className={`overflow-hidden border-t border-background-200 transition-all duration-300 md:hidden ${
          menuOpen ? "max-h-96" : "max-h-0 border-t-0"
        }`}
      >
        <div className="space-y-3 bg-background-50 px-4 py-4">
          <nav aria-label="移动端导航" className="flex flex-col">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  [
                    "rounded-md px-3 py-2.5 text-sm transition-colors",
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100",
                  ].join(" ")
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜瓜料 / 话题"
                aria-label="搜索瓜料"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm text-background-50"
            >
              搜索
            </button>
          </form>
        </div>
      </div>
    </header>
  );
}